// W35 (2026-10-20): read hooks for the W34 contact-property sinks.
//
//   useSubscriptionSummary(customerId)
//     -> GET /v1/contacts/:id/subscription-summary
//   useContactJsonRead(customerId, subpath, project)
//     -> generic same-origin reader. The Colic Coverage and Relationships
//        tabs build their own one-liners on it (GET /v1/contacts/:id/
//        colic-coverage, GET /v1/contacts/:id/relationships) so the three
//        W34 sinks share one fetch, one header contract and one error
//        shape.
//
// Same pattern as useContactProfile.ts: a RELATIVE fetch (nginx inside
// the workspace image proxies /v1/ to the api-server; the container has
// no VITE_RUNTIME_API_BASE_URL, so useScreenRuntime() is null and the
// older runtime.signed reads on these tabs never fired, MEASURED in
// scratchpad/SCREEN-AUDIT-2026-10-20.md rows 5, 6 and 9) with the session
// headers from buildSessionHeaders, gated by useContactReadGate so a
// preview (?preview=) or dev-mock session reports 'skipped' and keeps the
// design fixture.
//
// A failed read never falls back to fixture data. The caller renders an
// honest error with the api-server errorCode (or HTTP_<status>).
//
// Subscription summary body (MEASURED 2026-10-20 by W34 against the local
// api-server with the Key Vault PAT, scratchpad/w34-local-probes/
// jodi-subscription-summary.json; route: services/api-server/routes/
// stubs-w3c.mjs createContactSubscriptionSummaryHandler; sink:
// sinks/subscription-summary-sink.mjs toSubscriptionSummary):
//
//   Jodi 573130771181: { status:"active", productSku:"OMGX200",
//     productLabel:"Osteo-MAX + Gut X", frequencyDays:90,
//     frequencyLabel:"90 Days", nextChargeAt:"2027-01-18T00:00:00Z",
//     nextChargeDate:"2027-01-18", chargeStatus:"queued",
//     subscriptionStatusLabel:"Active",
//     payment:{type:"SHOP_PAY", last4:"9306", expMonth:1, expYear:2027,
//       valid:true}, lifetimeSubRevenue:294.45,
//     rechargeCustomerId:"269574269", loyalty:{points:0, tier:null},
//     source:"hubspot_contact_properties", contactId, result:"OK" }
//   Karla 573074999013 and Tere 572918887122: status:"none", every other
//     subscription field null (payment and loyalty objects of nulls).
//
// The sink lower-cases `status`; 'none' is its contract for "no
// recharge_subscription_status on the contact". Nothing here defaults a
// missing value into a label: a missing value stays null and the screen
// says so.

import { useEffect, useRef, useState } from 'react';

import { buildSessionHeaders, useContactReadGate } from './useContactProfile.js';

export type ContactReadStatus = 'loading' | 'ready' | 'error' | 'skipped';

export interface ContactReadResult<T> {
  status: ContactReadStatus;
  /** Defined only when status === 'ready'. */
  data: T | null;
  /** api-server `errorCode`, or HTTP_<status> / FETCH_FAILED / BAD_JSON / BAD_SHAPE. */
  errorCode?: string;
  /** api-server `detail` (or `message`) when the error body carried one. */
  errorDetail?: string;
  httpStatus?: number;
}

/**
 * Generic same-origin GET /v1/contacts/:id/<subpath>.
 *
 * `project` maps the parsed JSON body to the typed result and returns
 * null when the body is not the shape the route documents; that lands as
 * status 'error' / errorCode 'BAD_SHAPE' rather than as fixture data or
 * an empty card. The latest `project` is read through a ref so the fetch
 * effect depends only on the session and the path.
 */
export function useContactJsonRead<T>(
  customerId: string | null | undefined,
  subpath: string,
  project: (body: unknown) => T | null,
): ContactReadResult<T> {
  const gate = useContactReadGate(customerId);
  const { canFetch, sessionIdentity, sessionToken } = gate;
  const projectRef = useRef(project);
  projectRef.current = project;
  const [state, setState] = useState<ContactReadResult<T>>(() => ({
    status: canFetch ? 'loading' : 'skipped',
    data: null,
  }));
  const latest = useRef<string | null>(null);

  useEffect(() => {
    if (!canFetch || !customerId || !gate.session) {
      setState({ status: 'skipped', data: null });
      return;
    }
    const requestId = `${customerId}:${subpath}:${Date.now()}`;
    latest.current = requestId;
    setState({ status: 'loading', data: null });
    const headers = buildSessionHeaders(gate.session);
    const url = `/v1/contacts/${encodeURIComponent(customerId)}/${subpath}`;
    fetch(url, { method: 'GET', headers })
      .then(async (response) => {
        if (latest.current !== requestId) return;
        if (!response.ok) {
          let errorCode = `HTTP_${response.status}`;
          let errorDetail: string | undefined;
          try {
            const parsed = JSON.parse(await response.text()) as {
              errorCode?: unknown;
              detail?: unknown;
              message?: unknown;
            };
            if (parsed && typeof parsed.errorCode === 'string' && parsed.errorCode.length > 0) {
              errorCode = parsed.errorCode;
            }
            const detail =
              parsed && typeof parsed.detail === 'string'
                ? parsed.detail
                : parsed && typeof parsed.message === 'string'
                  ? parsed.message
                  : undefined;
            if (detail) errorDetail = detail;
          } catch {
            /* non-JSON or empty error body */
          }
          setState({
            status: 'error',
            data: null,
            errorCode,
            httpStatus: response.status,
            ...(errorDetail ? { errorDetail } : {}),
          });
          return;
        }
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          setState({ status: 'error', data: null, errorCode: 'BAD_JSON', httpStatus: response.status });
          return;
        }
        const data = projectRef.current(body);
        if (data === null) {
          setState({
            status: 'error',
            data: null,
            errorCode: 'BAD_SHAPE',
            errorDetail: `The /${subpath} response did not have the documented shape.`,
            httpStatus: response.status,
          });
          return;
        }
        setState({ status: 'ready', data, httpStatus: response.status });
      })
      .catch(() => {
        if (latest.current !== requestId) return;
        setState({ status: 'error', data: null, errorCode: 'FETCH_FAILED' });
      });
    return () => {
      if (latest.current === requestId) latest.current = null;
    };
    // sessionIdentity + sessionToken capture token refreshes without
    // closing over the full session object (same reasoning as
    // useContactProfile).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, customerId, subpath, sessionIdentity, sessionToken]);

  return state;
}

// ---------------------------------------------------------------------------
// Subscription summary
// ---------------------------------------------------------------------------

/** The sink's contract for "no recharge_subscription_status on the contact". */
export const SUBSCRIPTION_STATUS_NONE = 'none';

export interface SubscriptionPayment {
  /** Recharge payment type enumeration, e.g. SHOP_PAY. */
  type: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  /** recharge_has_valid_payment_method; null when HubSpot has no value. */
  valid: boolean | null;
}

export interface SubscriptionLoyalty {
  /** ll_points_balance; 0 is a real value, null means not on file. */
  points: number | null;
  /** ll_tier; null when LoyaltyLion has not assigned one. */
  tier: string | null;
}

export interface SubscriptionSummary {
  contactId: string | null;
  source: string | null;
  /** Lower-cased recharge_subscription_status, or 'none'. */
  status: string;
  productSku: string | null;
  productLabel: string | null;
  frequencyDays: number | null;
  frequencyLabel: string | null;
  nextChargeAt: string | null;
  /** YYYY-MM-DD from HubSpot next_charge_date. */
  nextChargeDate: string | null;
  chargeStatus: string | null;
  /** HubSpot subscription_status display label, e.g. "Active". */
  subscriptionStatusLabel: string | null;
  payment: SubscriptionPayment;
  lifetimeSubRevenue: number | null;
  rechargeCustomerId: string | null;
  loyalty: SubscriptionLoyalty;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const s = str(value);
  if (s === null) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function bool(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  const s = str(value);
  if (s === null) return null;
  if (s.toLowerCase() === 'true') return true;
  if (s.toLowerCase() === 'false') return false;
  return null;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Pure projection of the route body. Exported for tests. Returns null
 * when the body is not an object carrying a string `status`, which is the
 * one field the sink always sets.
 */
export function projectSubscriptionSummary(body: unknown): SubscriptionSummary | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const b = body as Record<string, unknown>;
  const status = str(b.status);
  if (status === null) return null;
  const payment = record(b.payment);
  const loyalty = record(b.loyalty);
  return {
    contactId: str(b.contactId),
    source: str(b.source),
    status: status.toLowerCase(),
    productSku: str(b.productSku),
    productLabel: str(b.productLabel),
    frequencyDays: num(b.frequencyDays),
    frequencyLabel: str(b.frequencyLabel),
    nextChargeAt: str(b.nextChargeAt),
    nextChargeDate: str(b.nextChargeDate),
    chargeStatus: str(b.chargeStatus),
    subscriptionStatusLabel: str(b.subscriptionStatusLabel),
    payment: {
      type: str(payment.type),
      last4: str(payment.last4),
      expMonth: num(payment.expMonth),
      expYear: num(payment.expYear),
      valid: bool(payment.valid),
    },
    lifetimeSubRevenue: num(b.lifetimeSubRevenue),
    rechargeCustomerId: str(b.rechargeCustomerId),
    loyalty: {
      points: num(loyalty.points),
      tier: str(loyalty.tier),
    },
  };
}

/** True when the contact has any subscription record (status is not 'none'). */
export function hasSubscriptionOnFile(summary: SubscriptionSummary | null | undefined): boolean {
  return !!summary && summary.status !== SUBSCRIPTION_STATUS_NONE;
}

export function useSubscriptionSummary(
  customerId: string | null | undefined,
): ContactReadResult<SubscriptionSummary> {
  return useContactJsonRead<SubscriptionSummary>(customerId, 'subscription-summary', projectSubscriptionSummary);
}
